/* v8 ignore file -- @preserve */
// Not easy to exhaustively test all routes and lazy imports

import {CrashlyticsRoutes} from '@firebase/crashlytics/react-router';
import type {Theme} from '@mui/material';
import {Suspense, lazy} from 'react';
import {Route} from 'react-router';
import {withErrorBoundary} from '../common/ErrorBoundary';
import {FullScreenLoading} from '../common/Loading';
import {firebaseApp} from '../firebase/firebase';
import {PrivateLayout as RawPrivateLayout} from '../layout/private/PrivateLayout';
import {PublicLayout as RawPublicLayout} from '../layout/public/PublicLayout';
import {Root} from '../main/Root';
import {ComponentWithoutChildren} from '../react/types';
import {FirebaseOutlet as RawFirebaseOutlet} from './FirebaseOutlet';
import {Paths} from './paths';
import {PrivateAppOutlet as RawPrivateAppOutlet} from './PrivateAppOutlet';
import {PublicAppOutlet as RawPublicAppOutlet} from './PublicAppOutlet';
import {makeRelative} from './routeUtils';

const RootWithErrorBoundary = withErrorBoundary(Root);
const FirebaseOutlet = withErrorBoundary(RawFirebaseOutlet);
const PrivateAppOutlet = withErrorBoundary(RawPrivateAppOutlet);
const PrivateLayout = withErrorBoundary(RawPrivateLayout);
const PublicAppOutlet = withErrorBoundary(RawPublicAppOutlet);
const PublicLayout = withErrorBoundary(RawPublicLayout);

const routeLoaders = {
  '/dashboard': () => import('../pages/dashboard/Dashboard'),
  '/login': () => import('../auth/Login'),
  '/logout': () => import('../auth/Logout'),
  '/pricing': () => import('../pages/pricing/Pricing'),
  '/reporting': () => import('../pages/reporting/Reporting'),
  '/settings': () => import('../pages/settings/Settings'),
  '/signup': () => import('../auth/Signup'),
  '/welcome': () => import('../pages/welcome/Welcome'),
} as const;

function preloadInitialRoute(): void {
  try {
    const pathname = window.location.pathname;
    const match = Object.keys(routeLoaders).find((path) => {
      if (path === pathname) {
        return true;
      }
      if (path.includes(':')) {
        const prefix = path.split(':')[0];
        return pathname.startsWith(prefix);
      }
      return false;
    });
    if (match) {
      void routeLoaders[match as keyof typeof routeLoaders]();
    } else if (pathname === '/' || pathname === '') {
      void routeLoaders['/dashboard']();
      void routeLoaders['/welcome']();
    }
  } catch {
    // Ignore in non-browser environments
  }
}

preloadInitialRoute();

const Dashboard = withErrorBoundary(lazy(routeLoaders['/dashboard']));
const Login = withErrorBoundary(lazy(routeLoaders['/login']));
const Logout = withErrorBoundary(lazy(routeLoaders['/logout']));
const NotFound = withErrorBoundary(lazy(() => import('../main/NotFound')));
const Pricing = withErrorBoundary(lazy(routeLoaders['/pricing']));
const Reporting = withErrorBoundary(lazy(routeLoaders['/reporting']));
const Settings = withErrorBoundary(lazy(routeLoaders['/settings']));
const Signup = withErrorBoundary(lazy(routeLoaders['/signup']));
const Welcome = withErrorBoundary(lazy(routeLoaders['/welcome']));

export const AppRoutes: ComponentWithoutChildren = () => {
  return (
    <Suspense fallback={<FullScreenLoading />}>
      <CrashlyticsRoutes firebaseApp={firebaseApp}>
        <Route element={<PublicAppOutlet />}>
          <Route element={<PublicLayout withoutContent />}>
            <Route
              element={
                <RootWithErrorBoundary>
                  <Welcome />
                </RootWithErrorBoundary>
              }
              index
            />
            <Route element={<Welcome />} path={Paths.Welcome} />
          </Route>
          <Route
            element={
              <PublicLayout
                contentProps={{
                  outletSx: {m: 2},
                  sx: {
                    bgcolor: (theme: Theme) =>
                      theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                  },
                }}
              />
            }
          >
            <Route element={<Pricing />} path={Paths.Pricing} />
          </Route>
        </Route>
        <Route element={<FirebaseOutlet />}>
          <Route element={<Logout />} path={Paths.Logout} />
          <Route element={<PublicAppOutlet />}>
            <Route
              element={
                <PublicLayout contentProps={{sx: {bgcolor: 'lightgrey'}}} publicTopBarProps={{hideLoginButton: true}} />
              }
            >
              <Route element={<Login />} path={Paths.Login} />
            </Route>
            <Route
              element={
                <PublicLayout
                  contentProps={{sx: {bgcolor: 'lightgrey'}}}
                  publicTopBarProps={{hideSignupButton: true}}
                />
              }
            >
              <Route element={<Signup />} path={Paths.Signup} />
            </Route>
          </Route>
          <Route element={<PrivateAppOutlet />} path="*">
            <Route element={<PrivateLayout />}>
              <Route element={<Dashboard />} path={makeRelative(Paths.Dashboard)} />
              <Route element={<Reporting />} path={makeRelative(Paths.Reporting)} />
              <Route element={<Settings />} path={makeRelative(Paths.Settings)} />
              <Route element={<NotFound />} path="*" />
            </Route>
          </Route>
        </Route>
      </CrashlyticsRoutes>
    </Suspense>
  );
};
